import { PageHero, SectionHeading } from "@/components/Section";
import Onboarding from "@/components/home/Onboarding";
import { Reveal } from "@/components/Reveal";
import { PortalButton } from "@/components/PortalButton";
import { RotateCcw, SlidersHorizontal, Check } from "lucide-react";

const REORDER = [
  "View previous orders", "Copy a previous order", "Adjust quantities",
  "Remove unavailable items", "Add new products", "Choose a delivery date", "Submit the updated order",
];

const PRICING_FACTORS = [
  "Purchase volume", "Case quantities", "Product mix", "Recurring orders",
  "Delivery location", "Delivery frequency", "Long-term business relationship",
];

export default function HowItWorks() {
  return (
    <>
      <PageHero
        chapter="How It Works"
        title="From account to first delivery in a few simple steps"
        lead="Quick registration. Flexible delivery. No complicated onboarding. Here's how food businesses get started with Brampton Cash & Carry."
      />

      <Onboarding />

      {/* Reordering */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <SectionHeading
            chapter="Reordering"
            overline="Routine Purchasing, Faster"
            title={<>Reorder your regular supplies in minutes</>}
            lead="Your restaurant probably orders many of the same products every week. Our reorder tools help you complete routine purchasing faster and with fewer errors."
          />
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-forest/15 bg-white p-7 shadow-hard">
              <div className="flex items-center gap-2 text-forest mb-5"><RotateCcw className="h-5 w-5" /> <span className="font-display text-lg">Reorder flow</span></div>
              <ul className="space-y-3">
                {REORDER.map((r, i) => (
                  <li key={r} className="flex items-center gap-3 text-sm text-forest/80">
                    <span className="grid place-items-center h-6 w-6 rounded-full bg-sage text-forest text-xs font-semibold shrink-0">{i + 1}</span>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Customer-specific pricing */}
      <section className="py-24 md:py-32 bg-forest text-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading
              dark
              chapter="Pricing"
              overline="Customer-Specific Pricing"
              title={<>Wholesale pricing built around your volume</>}
              lead="Pricing can be customized based on the factors that matter to your business. We don't display product prices publicly — approved accounts see their own pricing when logged in."
            />
            <div className="mt-8">
              <PortalButton variant="butter" testId="hiw-request-pricing-btn">Request Wholesale Pricing</PortalButton>
            </div>
          </div>
          <Reveal delay={0.1} className="grid sm:grid-cols-2 gap-3">
            {PRICING_FACTORS.map((p) => (
              <div key={p} className="flex items-center gap-2.5 rounded-2xl bg-forest-deep border border-white/10 px-4 py-3.5 text-sm text-cream/85">
                <SlidersHorizontal className="h-4 w-4 text-butter-deep shrink-0" /> {p}
              </div>
            ))}
          </Reveal>
        </div>
      </section>
    </>
  );
}
